import React, { useEffect, useRef, useState } from 'react';
import { useCustomTheme } from '../../contexts/ThemeContext';

const MOBILE_BREAKPOINT = 768;

const SpiritualParticlesCanvas = () => {
  const canvasRef = useRef(null);
  const { mode } = useCustomTheme();
  const [enabled, setEnabled] = useState(false);
  const [liteMode, setLiteMode] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncEnabled = () => {
      const reduceMotion = mediaQuery.matches;
      const isNarrow = window.innerWidth < MOBILE_BREAKPOINT;
      const isHidden = document.hidden;
      // Mobile gets a lighter particle pass; still off for reduced-motion / hidden tabs
      setLiteMode(isNarrow);
      setEnabled(!reduceMotion && !isHidden);
    };

    syncEnabled();
    mediaQuery.addEventListener('change', syncEnabled);
    window.addEventListener('resize', syncEnabled);
    document.addEventListener('visibilitychange', syncEnabled);

    return () => {
      mediaQuery.removeEventListener('change', syncEnabled);
      window.removeEventListener('resize', syncEnabled);
      document.removeEventListener('visibilitychange', syncEnabled);
    };
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const handleResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);

    const darkColors = [
      'rgba(255, 193, 7, ',
      'rgba(255, 140, 0, ',
      'rgba(212, 175, 55, ',
    ];

    const lightColors = [
      'rgba(217, 107, 0, ',
      'rgba(183, 129, 3, ',
      'rgba(234, 88, 12, '
    ];

    const colors = mode === 'dark' ? darkColors : lightColors;
    const density = liteMode ? 55000 : 25000;
    const maxParticles = liteMode ? 18 : 55;
    const particleCount = Math.min(Math.floor((width * height) / density), maxParticles);
    const particles = Array.from({ length: particleCount }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      radius: Math.random() * (liteMode ? 1.6 : 2.2) + 0.6,
      speedY: Math.random() * (liteMode ? 0.28 : 0.4) + 0.12,
      speedX: (Math.random() - 0.5) * (liteMode ? 0.15 : 0.25),
      opacity: Math.random() * 0.4 + 0.15,
      pulseSpeed: Math.random() * 0.012 + 0.004,
      pulseDir: 1,
      color: colors[Math.floor(Math.random() * colors.length)]
    }));

    const render = () => {
      ctx.clearRect(0, 0, width, height);

      particles.forEach((p) => {
        p.y -= p.speedY;
        p.x += p.speedX + Math.sin(p.y * 0.008) * 0.15;

        if (p.y < -10) {
          p.y = height + 10;
          p.x = Math.random() * width;
        }
        if (p.x < -10) p.x = width + 10;
        if (p.x > width + 10) p.x = -10;

        p.opacity += p.pulseSpeed * p.pulseDir;
        if (p.opacity >= (liteMode ? 0.45 : 0.6)) p.pulseDir = -1;
        if (p.opacity <= 0.12) p.pulseDir = 1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `${p.color}${p.opacity})`;
        // shadowBlur is expensive on mobile GPUs — desktop only
        if (!liteMode) {
          ctx.shadowBlur = p.radius * 3;
          ctx.shadowColor = `${p.color}0.8)`;
        }
        ctx.fill();
        if (!liteMode) ctx.shadowBlur = 0;
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
      ctx.clearRect(0, 0, width, height);
    };
  }, [mode, enabled, liteMode]);

  if (!enabled) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 0
      }}
    />
  );
};

export default SpiritualParticlesCanvas;
